import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Eye, PlayCircle } from 'lucide-react';
import { useAlerts, useReconhecerAlert, useResolverAlert, useRodarMotor } from '../hooks';
import { AlertBadge, ALERT_STATUS_LABELS, ALERT_TYPE_LABELS } from '../components/AlertBadge';
import { authService } from '../../../shared/lib/auth';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { formatarData } from '../utils/formatters';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { EmptyState } from '../../../shared/components/EmptyState';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { AlertSeverity, AlertStatus, AlertType } from '../api';
import { licencaUrl } from '../utils/links';

export default function AlertasPage() {
  const user = authService.getCurrentUser();
  const { can } = usePermissions();
  const canHandle = can(FEATURES.ativosAlertas, 'edit');
  const isRunAllowed = user?.role === 'admin' || user?.role === 'governance';

  const [type, setType] = useState<AlertType | ''>('');
  const [severity, setSeverity] = useState<AlertSeverity | ''>('');
  const [status, setStatus] = useState<AlertStatus | ''>('open');

  const alertsQuery = useAlerts({
    type: type || undefined,
    severity: severity || undefined,
    status: status || undefined,
    limit: 100,
  });
  const ack = useReconhecerAlert();
  const resolver = useResolverAlert();
  const runMotor = useRodarMotor();
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleAck(id: string) {
    setError(null);
    try {
      await ack.mutateAsync(id);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Falha ao reconhecer alerta');
    }
  }

  async function handleResolve(id: string) {
    setError(null);
    try {
      await resolver.mutateAsync(id);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Falha ao resolver alerta');
    }
  }

  async function handleRun() {
    setError(null); setBanner(null);
    try {
      const r = await runMotor.mutateAsync();
      if (r.skipped) setBanner('Motor pulou execução — outro run em andamento.');
      else setBanner(`Motor executado: ${r.created} novo(s), ${r.resolved} resolvido(s) automaticamente.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Falha ao rodar motor');
    }
  }

  const items = alertsQuery.data?.data ?? [];

  return (
    <PageContainer
      title="Central de alertas"
      breadcrumbs={[{ label: 'Ativos', to: '/ativos' }, { label: 'Alertas' }]}
      action={
        isRunAllowed && (
          <Button
            variant="secondary"
            icon={<PlayCircle className="h-4 w-4" />}
            loading={runMotor.isPending}
            onClick={handleRun}
            className="!border-green-300 !text-green-700 hover:!bg-green-50"
          >
            {runMotor.isPending ? 'Rodando…' : 'Rodar motor manualmente'}
          </Button>
        )
      }
    >
      {banner && (
        <Alert
          variant="info"
          action={<Button variant="ghost" size="sm" onClick={() => setBanner(null)}>fechar</Button>}
        >
          {banner}
        </Alert>
      )}
      {error && <Alert variant="error">{error}</Alert>}

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <AutocompleteSelect
            label="Tipo"
            options={[
              { value: '', label: 'Todos os tipos' },
              ...(Object.entries(ALERT_TYPE_LABELS) as Array<[string, string]>).map(([v, l]) => ({ value: v, label: l })),
            ]}
            value={type}
            onChange={(v) => setType(v as AlertType | '')}
          />
          <AutocompleteSelect
            label="Severidade"
            options={[
              { value: '', label: 'Todas as severidades' },
              { value: 'critical', label: 'Crítico' },
              { value: 'warning', label: 'Atenção' },
              { value: 'info', label: 'Informativo' },
            ]}
            value={severity}
            onChange={(v) => setSeverity(v as AlertSeverity | '')}
          />
          <AutocompleteSelect
            label="Status"
            options={[
              { value: '', label: 'Todos os status' },
              { value: 'open', label: 'Aberto' },
              { value: 'acknowledged', label: 'Reconhecido' },
              { value: 'resolved', label: 'Resolvido' },
            ]}
            value={status}
            onChange={(v) => setStatus(v as AlertStatus | '')}
          />
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">Severidade</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Tipo</th>
              <th className="px-4 py-2 text-left">Descrição</th>
              <th className="px-4 py-2 text-left">Escopo</th>
              <th className="px-4 py-2 text-left">Criado em</th>
              {canHandle && <th className="px-4 py-2 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {alertsQuery.isLoading && (
              <tr><td colSpan={7} className="py-6 text-center text-sm text-gray-500">Carregando...</td></tr>
            )}
            {!alertsQuery.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="p-0">
                  <EmptyState
                    title="Nenhum alerta encontrado"
                    description="Ajuste os filtros ou aguarde o próximo ciclo do motor."
                    size="compact"
                  />
                </td>
              </tr>
            )}
            {items.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-2"><AlertBadge severity={a.severity} /></td>
                <td className="px-4 py-2 text-gray-700">{ALERT_STATUS_LABELS[a.status]}</td>
                <td className="px-4 py-2 text-gray-700">{ALERT_TYPE_LABELS[a.type] ?? a.type}</td>
                <td className="px-4 py-2 text-gray-800">
                  <p className="font-medium">{a.title}</p>
                  <p className="text-xs text-gray-500">{a.message}</p>
                </td>
                <td className="px-4 py-2 text-xs text-gray-700">
                  {a.assetId && (
                    <Link to={`/ativos/${a.assetId}`} className="text-blue-600 hover:underline">
                      {a.assetDisplayCode ?? a.assetCode ?? a.assetId.slice(0, 8)}
                    </Link>
                  )}
                  {a.licenseId && (
                    <Link to={licencaUrl(a.licenseId)} className="text-blue-600 hover:underline">
                      {a.licenseName ?? a.licenseId.slice(0, 8)}
                    </Link>
                  )}
                  {a.custodianName && <p className="text-gray-500">{a.custodianName}</p>}
                  {a.stockItemId && (
                    <p className="text-gray-500">
                      {a.stockItemCode ? `${a.stockItemCode} · ` : ''}{a.stockItemDescription ?? a.stockItemId}
                    </p>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">{formatarData(a.createdAt)}</td>
                {canHandle && (
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {a.status === 'open' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Eye className="h-3 w-3" />}
                          onClick={() => handleAck(a.id)}
                          className="!border-yellow-300 !text-yellow-800 hover:!bg-yellow-50"
                        >
                          Reconhecer
                        </Button>
                      )}
                      {a.status !== 'resolved' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<CheckCircle className="h-3 w-3" />}
                          onClick={() => handleResolve(a.id)}
                          className="!border-green-300 !text-green-800 hover:!bg-green-50"
                        >
                          Resolver
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </PageContainer>
  );
}
